import React, { useEffect, useMemo, useState } from 'react';
import { BASE, jsonHeaders, useApi, fmtINR, fmtDay } from '../api';
import OfficeLogModal from '../OfficeLogModal';
import { Badge, Row, WidgetState, Donut, linkBtn } from './ui';

// Every widget gets { T, darkMode, go, role }:
//   T       Dashboard theme tokens        go(page)  navigate to a module
// Data comes from /api/dashboard/* routes, which enforce both widget access and
// role scope on the server, so a widget can only ever show what its role may see.

// ── Task deadlines ───────────────────────────────────────────────────────────
export function DueWidget({ T, go }) {
  const st = useApi('/api/dashboard/task-deadlines');
  const d = st.data || {};
  const cells = [
    ['Due today', d.due_today, '#f59e0b'], ['Due tomorrow', d.due_tomorrow, '#3b82f6'], ['Due in 7 days', d.due_week, '#10b981'],
    ['Overdue up to 7 days', d.overdue_week, '#f97316'], ['Overdue over 7 days', d.overdue_old, '#ef4444'],
  ];
  return (
    <WidgetState T={T} state={st}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 0 }}>
        {cells.map(([label, v, color], i) => (
          <button key={label} onClick={() => go('tasks')}
            style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 14px', borderLeft: i ? `1px solid ${T.border}` : 'none' }}>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color, lineHeight: 1.15 }}>{v ?? 0}</div>
            <div style={{ fontSize: 11.5, color: T.text3 }}>{label}</div>
          </button>
        ))}
      </div>
    </WidgetState>
  );
}

// ── Tasks overview ───────────────────────────────────────────────────────────
export function TasksWidget({ T, go }) {
  const st = useApi('/api/dashboard/task-overview');
  const d = st.data;
  const rows = d ? [
    ['Pending', d.pending, '#f59e0b'], ['On hold', d.hold, '#94a3b8'], ['In progress', d.in_progress, '#3b82f6'], ['Completed', d.completed, '#10b981'],
  ] : [];
  const open = d ? d.pending + d.in_progress + d.hold : 0;
  return (
    <WidgetState T={T} state={st}>
      {d && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}>
              {rows.map(([label, v, color], i) => (
                <Row key={label} T={T} last={i === rows.length - 1} onClick={() => go('tasks')}
                  title={<span><span style={{ color }}>●</span> {label}</span>} right={<b style={{ fontSize: 13, color: T.text }}>{v}</b>} />
              ))}
            </div>
            <Donut T={T} size={140} thickness={20} center={open} parts={rows.map(([, v, color]) => ({ value: v, color }))} />
          </div>
          {d.services.length > 0 && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Open work by service</div>
              {d.services.slice(0, 5).map(s => (
                <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  <span style={{ flex: 1, fontSize: 12, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                  <span style={{ fontSize: 11.5, color: '#6366f1', fontWeight: 700, width: 26, textAlign: 'right' }}>{s.open}</span>
                  <span style={{ width: 90, height: 6, borderRadius: 6, background: T.border, overflow: 'hidden' }}>
                    <i style={{ display: 'block', height: '100%', width: `${s.total ? Math.round(100 * s.done / s.total) : 0}%`, background: '#6366f1', borderRadius: 6 }} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </WidgetState>
  );
}

// Best-effort browser geolocation — resolves to null (never rejects) so a
// punch never gets stuck waiting on a permission prompt the user ignores.
function getGeoPosition(timeout = 8000) {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    const timer = setTimeout(() => resolve(null), timeout);
    navigator.geolocation.getCurrentPosition(
      pos => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); },
      () => { clearTimeout(timer); resolve(null); },
      { enableHighAccuracy: true, timeout, maximumAge: 30000 }
    );
  });
}

// ── My attendance ────────────────────────────────────────────────────────────
export function AttendanceWidget({ T }) {
  const st = useApi('/api/employees/my/punch-status');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [locating, setLocating] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 30000); return () => clearInterval(id); }, []);

  const geofenceOn = st.data?.geofence?.enabled;

  const act = async (path) => {
    setBusy(true); setErr('');
    try {
      setLocating(true);
      const loc = await getGeoPosition();
      setLocating(false);
      if (!loc) throw new Error('Could not get your location — please allow location access and try again.');
      const r = await fetch(`${BASE}/api/employees/${path}`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(loc) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.message || 'Could not update attendance');
      await st.reload();
    } catch (e) { setErr(e.message); } finally { setBusy(false); setLocating(false); }
  };

  const d = st.data;
  const sessions = (d && d.sessions) || [];
  const open = sessions.find(s => !s.punch_out);
  // Server total is as of the fetch; add the running session's time since then.
  const fetchedAt = useMemo(() => Date.now(), [d]);
  const mins = Math.round(((d && d.worked_minutes) || 0) + (open ? (Date.now() - fetchedAt) / 60000 : 0));
  const hm = m => `${Math.floor(m / 60)}h ${m % 60}m`;
  const clock = v => new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const btn = (bg) => ({ marginTop: 10, padding: '8px 18px', borderRadius: 10, border: 'none', background: bg, color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 });
  // Selfie clock-in needs the phone camera; "app only" firms block the browser outright.
  // Attendance only works inside the office geofence, so no office location = no clocking.
  const noOffice = d && d.is_employee && !geofenceOn;
  const appOnly = d && (noOffice || d.app_only || d.mode === 'selfie');
  const appNote = noOffice
    ? <div style={{ fontSize: 11.5, color: T.text3, marginTop: 8 }}>📍 Your firm hasn't set the office location yet. Ask your admin to set it in Settings → Work Schedule.</div>
    : <div style={{ fontSize: 11.5, color: T.text3, marginTop: 8 }}>📱 Clock in and out from the ChanakyaX Business app{d && d.mode === 'selfie' ? ' (selfie required)' : ''}</div>;

  return (
    <WidgetState T={T} state={st} empty={d && d.is_employee === false ? 'Attendance is not set up for your profile. Ask your admin to add you in HR.' : null}>
      <div data-tick={tick} style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.10),rgba(59,130,246,0.10))', borderRadius: 12, textAlign: 'center', padding: '16px 12px' }}>
        {!sessions.length && (<><div style={{ fontWeight: 600, color: T.text, fontSize: 13.5 }}>You are not clocked in yet</div>
          {appOnly ? appNote : <button style={btn('#6366f1')} disabled={busy} onClick={() => act('punch-in')}>{locating ? '📍 Locating…' : '▶ Clock in'}</button>}</>)}
        {open && (<><div style={{ fontWeight: 600, color: T.text, fontSize: 13.5 }}>Clocked in at {clock(open.punch_in)}</div><div style={{ fontSize: 24, fontWeight: 800, color: '#6366f1', margin: '4px 0 0' }}>{hm(mins)}</div>
          {appOnly ? appNote : <button style={btn('#ef4444')} disabled={busy} onClick={() => act('punch-out')}>{locating ? '📍 Locating…' : '■ Clock out'}</button>}</>)}
        {sessions.length > 0 && !open && (<><div style={{ fontWeight: 600, color: T.text, fontSize: 13.5 }}>Clocked out at {clock(sessions[sessions.length - 1].punch_out)}</div>
          {appOnly ? appNote : <button style={btn('#64748b')} disabled={busy} onClick={() => act('punch-in')}>{locating ? '📍 Locating…' : '▶ Clock in again'}</button>}</>)}
        {geofenceOn && !open && !appOnly && <div style={{ fontSize: 10.5, color: T.text3, marginTop: 6 }}>📍 You must be within {d.geofence.radius_m}m of {d.geofence.label} to clock in</div>}
        {err ? <div style={{ fontSize: 11.5, color: '#ef4444', marginTop: 8 }}>{err}</div> : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, padding: 12, border: `1px solid ${T.border}`, borderRadius: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(16,185,129,0.14)', display: 'grid', placeItems: 'center', color: '#059669', fontWeight: 800 }}>✓</div>
        <div><div style={{ fontSize: 11.5, color: T.text3 }}>Total worked today{sessions.length > 1 ? ` · ${sessions.length} sessions` : ''}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{hm(mins)}{d && d.required_minutes ? <span style={{ fontSize: 12, fontWeight: 600, color: T.text3 }}> / {hm(d.required_minutes)}</span> : null}</div></div>
      </div>
    </WidgetState>
  );
}

// ── Billing vs collections ───────────────────────────────────────────────────
const niceMax = v => { if (v <= 0) return 1; const p = Math.pow(10, Math.floor(Math.log10(v))), n = v / p; return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p; };
const axis = v => (v >= 1e7 ? `₹${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `₹${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `₹${Math.round(v / 1e3)}K` : `₹${Math.round(v)}`);

function BarChart({ data, T }) {
  const W = 600, H = 220, padL = 46, padB = 24, padT = 8;
  const max = niceMax(Math.max(1, ...data.map(d => Math.max(Number(d.total_billed) || 0, Number(d.total_collected) || 0))));
  const slot = (W - padL) / data.length, bw = Math.min(26, slot / 3);
  const y = v => padT + (H - padT - padB) * (1 - v / max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Billed versus collected per month">
      {[0, 0.25, 0.5, 0.75, 1].map(f => (
        <g key={f}>
          <line x1={padL} x2={W} y1={y(max * f)} y2={y(max * f)} stroke={T.border} />
          <text x={0} y={y(max * f) + 4} fontSize="10" fill={T.text3}>{f ? axis(max * f) : '₹0'}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const b = Number(d.total_billed) || 0, c = Number(d.total_collected) || 0;
        const x = padL + i * slot + slot / 2 - bw - 2;
        return (
          <g key={d.month_label}>
            <rect x={x} y={y(b)} width={bw} height={H - padB - y(b)} rx="4" fill="#6366f1" opacity="0.85"><title>{`${d.month_label} billed ${fmtINR(b)}`}</title></rect>
            <rect x={x + bw + 4} y={y(c)} width={bw} height={H - padB - y(c)} rx="4" fill="#10b981" opacity="0.85"><title>{`${d.month_label} collected ${fmtINR(c)}`}</title></rect>
            <text x={x + bw + 2} y={H - 8} textAnchor="middle" fontSize="10.5" fill={T.text3}>{d.month_label}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function BillingWidget({ T }) {
  const [months, setMonths] = useState(6);
  const st = useApi(`/api/dashboard/finance-monthly?months=${months}`);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: T.text3 }}><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: '#6366f1', marginRight: 4 }} />Billed</span>
        <span style={{ fontSize: 12, color: T.text3 }}><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: '#10b981', marginRight: 4 }} />Collected</span>
        <div style={{ marginLeft: 'auto', display: 'flex', background: T.bg3, borderRadius: 8, padding: 2 }}>
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)} style={{ border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: months === m ? T.cardBg : 'transparent', color: months === m ? '#6366f1' : T.text3 }}>{m}M</button>
          ))}
        </div>
      </div>
      <WidgetState T={T} state={st}>{Array.isArray(st.data) && st.data.length > 0 && <BarChart data={st.data} T={T} />}</WidgetState>
    </>
  );
}

// ── Revenue snapshot ─────────────────────────────────────────────────────────
export function RevenueWidget({ T, go }) {
  const st = useApi('/api/dashboard/revenue-snapshot');
  const d = st.data;
  const items = d ? [['Billed this month', d.billed_month, '#6366f1'], ['Collected this month', d.collected_month, '#10b981'], ['Outstanding', d.outstanding, '#ef4444']] : [];
  return (
    <WidgetState T={T} state={st}>
      {items.map(([label, v, color], i) => (
        <button key={label} onClick={() => go('finance')}
          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', borderBottom: i < 2 ? `1px solid ${T.border}` : 'none' }}>
          <div style={{ fontSize: 11.5, color: T.text3 }}>{label}</div>
          <div style={{ fontSize: 23, fontWeight: 800, color, letterSpacing: '-0.02em' }}>{fmtINR(v)}</div>
        </button>
      ))}
      {d && d.overdue_count > 0 && <div style={{ marginTop: 6 }}><Badge tone="red">{d.overdue_count} overdue invoice{d.overdue_count > 1 ? 's' : ''}</Badge></div>}
    </WidgetState>
  );
}

// ── Pending invoicing ────────────────────────────────────────────────────────
export function UnbilledWidget({ T, go }) {
  const st = useApi('/api/dashboard/task-billing-stats');
  const d = st.data || {};
  const box = (bg, icon, title, sub, value, color, page, size = 26) => (
    <div onClick={() => go(page)} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 12px', padding: 12, border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 10, cursor: 'pointer' }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: bg, display: 'grid', placeItems: 'center', fontSize: 17, flex: 'none' }}>{icon}</div>
      <div style={{ flex: '1 1 110px', minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</div><div style={{ fontSize: 11, color: T.text3 }}>{sub}</div></div>
      <div style={{ fontSize: size, fontWeight: 800, color, flex: 'none', marginLeft: 'auto' }}>{value}</div>
    </div>
  );
  return (
    <WidgetState T={T} state={st}>
      {box('rgba(99,102,241,0.14)', '🧾', 'Unbilled tasks', 'Work done but not yet billed', d.unbilled_count ?? 0, '#6366f1', 'tasks')}
      {box('rgba(239,68,68,0.12)', '⏳', 'Billed, not yet paid', `${d.billed_count ?? 0} billed tasks`, fmtINR(d.pending_value), '#ef4444', 'finance', 20)}
    </WidgetState>
  );
}

// ── Today's actions ──────────────────────────────────────────────────────────
export function ActionsWidget({ T, go }) {
  const st = useApi('/api/dashboard/actions');
  const d = st.data;
  const none = d && !d.tasks.length && !d.renewals.length && !d.invoices.length;
  return (
    <WidgetState T={T} state={st} empty={none ? 'All clear. Nothing urgent right now.' : null}>
      {d && (
        <>
          {d.tasks.map(t => <Row key={`t${t.id}`} T={T} onClick={() => go('tasks')} title={t.title} sub={`${t.client_name || 'No client'} · due ${fmtDay(t.due_date)}`} right={<Badge tone="red">Overdue</Badge>} />)}
          {d.renewals.map(r => <Row key={`r${r.id}`} T={T} onClick={() => go('renewals')} title={r.client_name || 'Client'} sub={r.renewal_type}
            right={<Badge tone={r.days_left < 0 ? 'red' : 'amber'}>{r.days_left < 0 ? 'Expired' : r.days_left === 0 ? 'Today' : `${r.days_left}d left`}</Badge>} />)}
          {d.invoices.map(i => <Row key={`i${i.id}`} T={T} onClick={() => go('finance')} title={i.client_name || 'Client'} sub={i.invoice_number} right={<Badge tone="blue">{fmtINR(i.balance_due)} due</Badge>} last />)}
        </>
      )}
    </WidgetState>
  );
}

// ── Renewals due ─────────────────────────────────────────────────────────────
export function RenewalsWidget({ T, go }) {
  const st = useApi('/api/dashboard/renewals-due');
  const d = st.data;
  return (
    <WidgetState T={T} state={st} empty={d && !d.items.length ? 'No renewals due in the next 30 days.' : null}>
      {d && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <Badge tone={d.expired ? 'red' : 'grey'}>{d.expired} expired</Badge>
            <Badge tone={d.expiring ? 'amber' : 'grey'}>{d.expiring} due in 30 days</Badge>
          </div>
          {d.items.map((r, i) => <Row key={r.id} T={T} last={i === d.items.length - 1} onClick={() => go('renewals')} title={r.client_name || 'Client'} sub={`${r.renewal_type} · ${fmtDay(r.expiry_date)}`}
            right={<Badge tone={r.days_left < 0 ? 'red' : r.days_left <= 7 ? 'amber' : 'blue'}>{r.days_left < 0 ? 'Expired' : r.days_left === 0 ? 'Today' : `${r.days_left}d`}</Badge>} />)}
        </>
      )}
    </WidgetState>
  );
}

// ── Recent leads ─────────────────────────────────────────────────────────────
const STAGE_TONE = { new: 'blue', contacted: 'amber', won: 'green', lost: 'red' };
export function LeadsWidget({ T, go }) {
  const st = useApi('/api/dashboard/recent-leads');
  const list = Array.isArray(st.data) ? st.data : [];
  return (
    <WidgetState T={T} state={st} empty={st.data && !list.length ? 'No leads yet.' : null}>
      {list.map((l, i) => (
        <Row key={l.id} T={T} last={i === list.length - 1} onClick={() => go('crm')} title={l.name || l.company} sub={l.phone || l.company}
          right={<Badge tone={STAGE_TONE[String(l.stage).toLowerCase()] || 'grey'}>{String(l.stage || 'New').replace(/_/g, ' ')}</Badge>} />
      ))}
    </WidgetState>
  );
}

// ── Clients by type ──────────────────────────────────────────────────────────
const PALETTE = ['#eeb022', '#3aa0d5', '#10b981', '#8b5cf6', '#f97316', '#94a3b8'];
export function ClientsWidget({ T }) {
  const st = useApi('/api/dashboard/client-types');
  const list = (Array.isArray(st.data) ? st.data : []).map(x => ({ name: x.client_type || 'other', value: parseInt(x.count, 10) || 0 }));
  const top = list.slice(0, 5), rest = list.slice(5).reduce((a, x) => a + x.value, 0);
  if (rest) top.push({ name: 'other types', value: rest });
  const total = list.reduce((a, x) => a + x.value, 0);
  return (
    <WidgetState T={T} state={st} empty={st.data && !total ? 'No clients yet.' : null}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <Donut T={T} size={120} thickness={20} center={total} parts={top.map((x, i) => ({ value: x.value, color: PALETTE[i % PALETTE.length] }))} />
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          {top.map((x, i) => (
            <div key={x.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0', color: T.text2 }}>
              <i style={{ width: 9, height: 9, borderRadius: 3, background: PALETTE[i % PALETTE.length], flex: 'none' }} />
              <span style={{ flex: 1, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(x.name).replace(/_/g, ' ')}</span>
              <b style={{ color: T.text }}>{x.value}</b>
            </div>
          ))}
        </div>
      </div>
    </WidgetState>
  );
}

// ── Team in office ───────────────────────────────────────────────────────────
export function OfficeWidget({ T, darkMode, role }) {
  const st = useApi('/api/dashboard/hr-today', { interval: 30000 });
  const [showLog, setShowLog] = useState(false);
  const people = (st.data && st.data.logged_in_today) || [];
  return (
    <>
      <WidgetState T={T} state={st} empty={st.data && !people.length ? 'Nobody is signed in right now.' : null}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {people.map(u => (
            <span key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 7, background: T.bg3, borderRadius: 20, padding: '4px 11px 4px 4px', fontSize: 12, fontWeight: 600, color: T.text }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 10.5, fontWeight: 700, overflow: 'hidden' }}>
                {u.avatar_url ? <img src={`${BASE}${u.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%' }} /> : (u.full_name || 'U').charAt(0).toUpperCase()}
              </span>
              {u.full_name}
              <i style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 0 3px rgba(16,185,129,0.25)' }} />
            </span>
          ))}
        </div>
      </WidgetState>
      {role === 'admin' && <div style={{ marginTop: 12 }}><button style={linkBtn(T)} onClick={() => setShowLog(true)}>View login / logout log →</button></div>}
      {showLog && <OfficeLogModal darkMode={darkMode} T={T} onClose={() => setShowLog(false)} />}
    </>
  );
}

// ── Compliance calendar ──────────────────────────────────────────────────────
export function ComplianceWidget({ T }) {
  const st = useApi('/api/dashboard/compliance-upcoming');
  const items = (st.data && st.data.items) || [];
  return (
    <WidgetState T={T} state={st} empty={st.data && !items.length ? 'No due dates set. Add them in Settings → Compliance Calendar.' : null}>
      {items.map((c, i) => (
        <Row key={c.id} T={T} last={i === items.length - 1} title={c.name} sub={c.category}
          right={<span style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{fmtDay(c.due_date)}</div>
            <Badge tone={c.days_left <= 3 ? 'red' : c.days_left <= 10 ? 'amber' : 'grey'}>{c.days_left === 0 ? 'Today' : c.days_left === 1 ? 'Tomorrow' : `${c.days_left} days`}</Badge>
          </span>} />
      ))}
    </WidgetState>
  );
}
